import { useState } from 'react';
import { NewsArticle, NewsCategory } from '../types';

interface ArticleCategoryModalProps {
  article: NewsArticle;
  categories: NewsCategory[];
  onSave: (articleId: string, categoryIds: string[], categoriesLocked: boolean) => Promise<void>;
  onClose: () => void;
}

export default function ArticleCategoryModal({
  article,
  categories,
  onSave,
  onClose,
}: ArticleCategoryModalProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(article.categoryIds || []);
  const [locked, setLocked] = useState(article.categoriesLocked ?? false);
  const [saving, setSaving] = useState(false);

  const handleToggleCategory = (categoryId: string) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
    // Automatically lock when the admin manually changes the selection
    setLocked(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(article.id, selectedCategories.length > 0 ? selectedCategories : [], locked);
      onClose();
    } catch (error) {
      console.error('Error saving categories:', error);
      alert('Failed to save categories');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="modal modal-blur fade show"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Article Categories</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Article</label>
                <div className="text-muted small">{article.title}</div>
              </div>
              <div className="mb-3">
                <label className="form-label">Select Categories</label>
                <div className="form-selectgroup form-selectgroup-boxes d-flex flex-column gap-2">
                  {categories.map(category => (
                    <label key={category.id} className="form-selectgroup-item flex-fill">
                      <input
                        type="checkbox"
                        className="form-selectgroup-input"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => handleToggleCategory(category.id)}
                      />
                      <div className="form-selectgroup-label d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <span>{category.name || category.id}</span>
                        </div>
                        {selectedCategories.includes(category.id) && (
                          <span className="badge bg-primary text-white">Selected</span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
                {categories.length === 0 && (
                  <div className="text-muted small mt-2">No categories available</div>
                )}
              </div>
              <div className="text-muted small">
                Selected: {selectedCategories.length} categor
                {selectedCategories.length === 1 ? 'y' : 'ies'}
              </div>
              <div className="mt-3">
                <label className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={locked}
                    onChange={e => setLocked(e.target.checked)}
                  />
                  <span className="form-check-label">Prevent automatic reassignment</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link link-secondary"
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
